import { Logo } from "@/components/Logo";
import { A4Page } from "@/components/production/printKit";

export const CASE_REF = "MIT/SPORE/C3/2114";

// Decorative barcode: deterministic bar widths derived from the reference text.
function Barcode({ text }) {
  const bars = [];
  let x = 0;
  [...`*${text}*`].forEach((ch, i) => {
    const c = ch.charCodeAt(0);
    for (let k = 0; k < 5; k += 1) {
      const w = ((c >> k) & 1) + 1 + ((i + k) % 3 === 0 ? 1 : 0);
      if (k % 2 === 0) bars.push(<rect key={`${i}-${k}`} x={x} y={0} width={w} height={46} />);
      x += w + 1;
    }
    x += 2;
  });
  return (
    <svg viewBox={`0 0 ${x} 46`} width={250} height={46} preserveAspectRatio="none" aria-hidden className="block" data-testid="label-barcode">
      <g fill="#0b0c0c">{bars}</g>
    </svg>
  );
}

function CoverLabel({ file, kicker, name, sub, spare }) {
  const ref = `${CASE_REF}/F${file}`;
  return (
    <div className="cover-label" data-testid={`cover-label-${file}`}>
      <span className="crop tl" /><span className="crop tr" /><span className="crop bl" /><span className="crop br" />
      <div className="cover-inner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo height={34} />
            <span className="text-[14px] font-bold text-[#505a5f] border-l-2 border-[#b1b4b6] pl-4">Major Investigation Team</span>
          </div>
          <span className="text-[13px] font-bold tracking-wide text-[#0033A1]">FILE {file} / 05</span>
        </div>
        <div className="cover-band">RESTRICTED — CASE FILE</div>
        <div className="flex items-baseline gap-3 mt-3 text-[16px]">
          <strong className="text-[#0033A1] text-[18px] font-black">Operation SPORE</strong>
          <span className="text-[#505a5f]">·</span>
          <span>Inquiry: Death of Dr Ebaro Monroe</span>
        </div>
        <div className="flex-1 flex flex-col justify-center border-l-[10px] border-[#0033A1] pl-5 my-3">
          <div className="text-[17px] font-black tracking-[0.14em] text-[#0033A1]">{kicker}</div>
          {spare ? (
            <>
              <div className="write-line !h-[44px] w-[88%]" />
              <div className="write-line !h-[34px] w-[70%]" />
            </>
          ) : (
            <>
              <div className="text-[46px] font-black leading-[1.02] mt-1 uppercase">{name}</div>
              <div className="text-[21px] mt-1.5">{sub}</div>
            </>
          )}
        </div>
        <div className="flex items-end justify-between gap-6">
          <div className="text-[13px] leading-snug">
            <div className="font-bold text-[15px]">Case reference: {CASE_REF}</div>
            <div>File number: F{file}</div>
            <div className="mt-1.5 font-bold uppercase tracking-wide">Handle as evidence</div>
            <div className="uppercase tracking-wide">Do not remove from the incident room</div>
          </div>
          <div className="text-center">
            <Barcode text={ref} />
            <div className="font-mono text-[11px] tracking-[0.2em] mt-1">{ref}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function labelList(c) {
  const suspects = c.order.map((sid, i) => ({
    id: `label-${sid}`, file: String(i + 1).padStart(2, "0"), kicker: `SUSPECT FILE ${String(i + 1).padStart(2, "0")}`,
    name: c.suspects[sid].name, sub: c.suspects[sid].role, title: `Cover label · Suspect file ${String(i + 1).padStart(2, "0")} — ${c.suspects[sid].name}`,
  }));
  const info = { id: "label-info", file: "05", kicker: "INVESTIGATION BRIEFING", name: "Info pack", sub: "Read first", title: "Cover label · Investigation briefing — Info pack" };
  return [...suspects, info];
}

export const SPARE = { id: "label-spare", file: "__", kicker: "SUSPECT FILE __", spare: true };

export const LabelSheet = ({ labels }) => (
  <A4Page>
    <div className="a4-fill label-sheet">
      {labels.map((l) => (l ? <CoverLabel key={l.id} {...l} /> : null))}
    </div>
  </A4Page>
);

export function labelsPack(c) {
  const all = labelList(c);
  const sheets = [[all[0], all[1]], [all[2], all[3]], [all[4], SPARE]];
  return {
    id: "labels", kind: "labels", title: "Case file cover labels", file: "Case_file_cover_labels",
    printLabel: "Print all labels", pdfLabel: "Download all labels (PDF)",
    note: "Print on plain A4 at 100%, cut along the dashed lines and stick on the front of each folder.",
    whole: { id: "labels-all", title: "All case file cover labels", pages: () => sheets.map((s, i) => <LabelSheet key={i} labels={s} />) },
    assets: all.map((l) => ({ id: l.id, title: l.title, pages: () => [<LabelSheet key="s" labels={[l]} />] })),
  };
}
